/**
 * POD Shop Controller – Handles the full "Buy Physical" flow.
 *
 * Steps: Product Selection → Configure (size/qty) → Shipping → Payment → Confirmation
 */

import { t } from "../i18n.ts";

interface MockupConfig {
  type: string;
  printArea: { x: number; y: number; w: number; h: number };
  allowPrintScale: boolean;
}

interface ProductTemplate {
  id: string;
  name: string;
  category: string;
  description: string;
  icon: string;
  baseCostEur: number;
  availability?: ProductAvailability;
  mockup?: MockupConfig;
  sizes: { id: string; label: string; availability?: ProductAvailability }[];
}

interface ProductAvailability {
  available: boolean;
  reason?: string;
}

interface PriceInfo {
  baseCost: number;
  margin: number;
  totalPrice: number;
  currency: string;
  formattedPrice: string;
  formattedProfit: string;
}

export class PodShopController {
  private products: ProductTemplate[] = [];
  private isDemoMode = true;
  private stripeReady = false;
  private stripeMode: "test" | "live" | "none" = "none";
  private webhookReady = false;
  private allowOrderPhysical = true;
  private currentStep: "product" | "config" | "checkout" | "payment" | "confirm" = "product";
  private selectedProduct: ProductTemplate | null = null;
  private selectedSizeId = "";
  private quantity = 1;
  private printScale = 1.0;
  private currentPrice: PriceInfo | null = null;
  private designAssetId = "";
  private shippingCountry = "DK";
  private lockedShippingAddress: any = null;
  private lockedItems: any[] = [];

  private modal!: HTMLElement;
  private getDesignAssetId: (printScale: number) => Promise<string>;

  constructor(getDesignAssetId: (printScale: number) => Promise<string>) {
    this.getDesignAssetId = getDesignAssetId;
    this.modal = document.getElementById("pod-shop-modal")!;
    if (this.modal) {
      this.setupEventListeners();
    }
  }

  // ─── Public API ─────────────────────────────────────────────────

  setAllowOrderPhysical(allowed: boolean) {
    this.allowOrderPhysical = allowed;
  }

  setShopStatus(status: {
    allowOrderPhysical: boolean;
    stripeReady?: boolean;
    stripeMode?: "test" | "live" | "none";
    webhookReady?: boolean;
  }) {
    this.allowOrderPhysical = status.allowOrderPhysical;
    this.stripeReady = !!status.stripeReady;
    this.stripeMode = status.stripeMode || "none";
    this.webhookReady = !!status.webhookReady;
    this.updateEnvironmentBadge();
  }

  async open() {
    if (!this.modal) return;

    this.shippingCountry = this.getSavedShippingCountry();
    this.syncCountryControls();

    // Reset to step 1
    this.goToStep("product");
    this.selectedProduct = null;
    this.selectedSizeId = "";
    this.quantity = 1;
    this.designAssetId = "";
    this.lockedShippingAddress = null;
    this.lockedItems = [];
    this.updateActionButton();

    this.modal.classList.remove("hidden");
    document.body.style.overflow = "hidden";

    this.renderLoadingProducts();
    this.applyTranslations();
    await this.loadCatalog();
  }

  close() {
    this.requestClose();
  }

  private requestClose() {
    if (this.modal.classList.contains("hidden")) return;
    if (this.currentStep !== "confirm" && !globalThis.confirm(this.tr("pod.confirmClose"))) {
      return;
    }
    this.closeNow();
  }

  private closeNow() {
    this.modal.classList.add("hidden");
    document.body.style.overflow = "";
  }

  refreshLanguage() {
    this.applyTranslations();
    this.updateActionButton();
    if (this.currentStep === "product" && this.products.length > 0) {
      this.renderProducts(this.getActiveCategory());
    }
  }

  // ─── Event Listeners ───────────────────────────────────────────

  private setupEventListeners() {
    // Close
    document.getElementById("pod-shop-close")?.addEventListener("click", () => this.requestClose());
    document.getElementById("pod-shop-backdrop")?.addEventListener(
      "click",
      () => this.requestClose(),
    );
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && !this.modal.classList.contains("hidden")) {
        this.requestClose();
      }
    });

    // Category tabs
    this.modal.querySelectorAll(".pod-cat-tab").forEach((tab) => {
      tab.addEventListener("click", (e: any) => {
        const cat = e.currentTarget.dataset.cat;
        this.modal.querySelectorAll(".pod-cat-tab").forEach((t) => t.classList.remove("active"));
        e.currentTarget.classList.add("active");
        this.renderProducts(cat);
      });
    });

    // Back buttons
    document.getElementById("pod-back-to-products")?.addEventListener("click", () => {
      this.goToStep("product");
      this.updateActionButton();
    });
    document.getElementById("pod-back-to-config")?.addEventListener("click", () => {
      this.goToStep("config");
      this.updateActionButton();
    });
    document.getElementById("pod-back-to-shipping")?.addEventListener("click", () => {
      this.goToStep("checkout");
      this.updateActionButton();
    });

    // Quantity
    document.getElementById("pod-qty-minus")?.addEventListener("click", () => {
      if (this.quantity > 1) {
        this.quantity--;
        this.updateQuantityDisplay();
        this.updatePrice();
      }
    });
    document.getElementById("pod-qty-plus")?.addEventListener("click", () => {
      if (this.quantity < 99) {
        this.quantity++;
        this.updateQuantityDisplay();
        this.updatePrice();
      }
    });

    // Action button
    document.getElementById("pod-action-btn")?.addEventListener("click", () => this.handleAction());

    // Terms checkbox
    document.getElementById("pod-terms-check")?.addEventListener("change", () => {
      this.updateActionButton();
    });

    document.getElementById("pod-product-country")?.addEventListener("change", async (e: any) => {
      this.shippingCountry = e.target.value || "DK";
      this.selectedProduct = null;
      this.selectedSizeId = "";
      this.syncCountryControls();
      this.renderLoadingProducts();
      await this.loadCatalog();
      this.updateActionButton();
    });

    document.getElementById("pod-country")?.addEventListener("change", async (e: any) => {
      this.shippingCountry = e.target.value || "DK";
      this.selectedProduct = null;
      this.selectedSizeId = "";
      this.syncCountryControls();
      this.renderLoadingProducts();
      await this.loadCatalog();
      this.goToStep("product");
      this.updateActionButton();
    });

    document.getElementById("pod-print-scale-section")?.addEventListener("click", (e: any) => {
      const btn = (e.target as Element).closest(".pod-scale-btn") as HTMLElement | null;
      if (!btn) return;
      const scale = parseFloat(btn.dataset.scale || "1");
      if (!isNaN(scale)) {
        this.printScale = scale;
        this.designAssetId = "";
        this.updateScaleButtons();
        this.drawMockup();
      }
    });
  }

  // ─── Catalog ────────────────────────────────────────────────────

  private async loadCatalog() {
    try {
      const response = await fetch(
        `/api/pod/catalog?country=${encodeURIComponent(this.shippingCountry)}`,
      );
      if (!response.ok) return;
      const data = await response.json();
      this.products = data.products || [];
      this.isDemoMode = data.demoMode;
      this.updateEnvironmentBadge();

      this.renderProducts(this.getActiveCategory());
    } catch (e) {
      console.error("Failed to load POD catalog:", e);
    }
  }

  private renderLoadingProducts() {
    const grid = document.getElementById("pod-product-grid");
    if (!grid) return;

    grid.innerHTML = `
      <div class="col-span-full flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 text-sm text-slate-600 dark:text-slate-300">
        <span class="material-symbols-outlined animate-spin text-primary text-[20px]">sync</span>
        <span>${
      this.tr("pod.findingProducts", { country: this.getCountryName(this.shippingCountry) })
    }</span>
      </div>
    `;
  }

  private getActiveCategory(): string {
    const activeTab = this.modal.querySelector(".pod-cat-tab.active") as HTMLElement | null;
    return activeTab?.dataset.cat || "wall_art";
  }

  private renderProducts(category: string) {
    const grid = document.getElementById("pod-product-grid");
    if (!grid) return;

    const filtered = this.products.filter((p) => p.category === category);
    grid.innerHTML = filtered
      .map(
        (p) => {
          const available = p.availability?.available !== false;
          const reason = p.availability?.reason ||
            this.tr("pod.notAvailableIn", { country: this.getCountryName(this.shippingCountry) });
          return `
      <div class="pod-product-card rounded-xl bg-white dark:bg-slate-700/50 p-4 ${
            this.selectedProduct?.id === p.id ? "selected" : ""
          } ${available ? "" : "opacity-45 grayscale cursor-not-allowed"}"
           data-product-id="${p.id}">
        <div class="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-100 to-indigo-100 dark:from-violet-900/30 dark:to-indigo-900/30 flex items-center justify-center mb-3">
          <span class="material-symbols-outlined text-primary text-[24px]">${p.icon}</span>
        </div>
        <h4 class="font-bold text-sm text-slate-900 dark:text-white">${p.name}</h4>
        <p class="text-xs text-slate-500 mt-1 line-clamp-2">${p.description}</p>
        ${
            available
              ? `<p class="text-sm font-bold text-primary mt-2">${
                this.tr("pod.fromPrice", { price: `€${p.baseCostEur.toFixed(2)}` })
              }</p>`
              : `<p class="text-xs font-bold text-rose-600 dark:text-rose-400 mt-2">${reason}</p>`
          }
      </div>
    `;
        },
      )
      .join("");

    // Add click handlers
    grid.querySelectorAll(".pod-product-card").forEach((card: any) => {
      card.addEventListener("click", () => {
        const productId = card.dataset.productId;
        this.selectProduct(productId);
      });
    });
  }

  private selectProduct(productId: string) {
    this.selectedProduct = this.products.find((p) => p.id === productId) || null;
    if (!this.selectedProduct) return;
    if (this.selectedProduct.availability?.available === false) {
      this.selectedProduct = null;
      this.updateActionButton();
      return;
    }

    // Mark selected in grid
    this.modal.querySelectorAll(".pod-product-card").forEach((card: any) => {
      card.classList.toggle("selected", card.dataset.productId === productId);
    });

    this.updateActionButton();
  }

  // ─── Configuration Step ─────────────────────────────────────────

  private showConfigStep() {
    if (!this.selectedProduct) return;

    const nameEl = document.getElementById("pod-selected-name");
    const descEl = document.getElementById("pod-selected-desc");
    if (nameEl) nameEl.textContent = this.selectedProduct.name;
    if (descEl) descEl.textContent = this.selectedProduct.description;

    // Render size options
    const sizeContainer = document.getElementById("pod-size-options");
    if (sizeContainer) {
      sizeContainer.innerHTML = this.selectedProduct.sizes
        .map(
          (s) => {
            const available = s.availability?.available !== false;
            const reason = s.availability?.reason ||
              this.tr("pod.notAvailableIn", { country: this.getCountryName(this.shippingCountry) });
            return `<button class="pod-size-btn ${
              available ? "" : "opacity-45 cursor-not-allowed"
            }" data-size-id="${s.id}" ${available ? "" : "disabled"} title="${reason}">${s.label}${
              available ? "" : ` · ${this.tr("pod.unavailable")}`
            }</button>`;
          },
        )
        .join("");

      const firstAvailableSize = this.selectedProduct.sizes.find((s) =>
        s.availability?.available !== false
      );
      this.selectedSizeId = firstAvailableSize?.id || "";
      const activeBtn = sizeContainer.querySelector(`[data-size-id="${this.selectedSizeId}"]`);
      activeBtn?.classList.add("active");

      sizeContainer.querySelectorAll(".pod-size-btn").forEach((btn: any) => {
        btn.addEventListener("click", () => {
          if (btn.disabled) return;
          sizeContainer.querySelectorAll(".pod-size-btn").forEach((b) =>
            b.classList.remove("active")
          );
          btn.classList.add("active");
          this.selectedSizeId = btn.dataset.sizeId;
          this.updatePrice();
        });
      });
    }

    // Draw product mockup
    this.setupPrintScaleSection();
    this.drawMockup();

    // Reset quantity
    this.quantity = 1;
    this.updateQuantityDisplay();
    if (this.selectedSizeId) {
      this.updatePrice();
    }
  }

  private setupPrintScaleSection() {
    const section = document.getElementById("pod-print-scale-section");
    if (!section) return;
    const mockup = this.selectedProduct?.mockup;
    if (!mockup?.allowPrintScale) {
      section.classList.add("hidden");
      return;
    }
    section.classList.remove("hidden");
    this.printScale = 1.0;
    this.updateScaleButtons();
  }

  private updateScaleButtons() {
    const section = document.getElementById("pod-print-scale-section");
    section?.querySelectorAll(".pod-scale-btn").forEach((btn: any) => {
      const s = parseFloat(btn.dataset.scale);
      const active = Math.abs(s - this.printScale) < 0.01;
      btn.classList.toggle("border-primary", active);
      btn.classList.toggle("text-primary", active);
      btn.classList.toggle("font-semibold", active);
      btn.classList.toggle("bg-primary/5", active);
    });
  }

  private drawMockup() {
    const miniCanvas = document.getElementById("pod-mini-preview") as HTMLCanvasElement;
    const mainCanvas = document.getElementById("poster-canvas") as HTMLCanvasElement;
    if (!miniCanvas) return;

    miniCanvas.width = 200;
    miniCanvas.height = 200;
    const ctx = miniCanvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, 200, 200);
    const mockup = this.selectedProduct?.mockup;

    this.drawProductSilhouette(ctx, mockup?.type || "poster");

    if (!mainCanvas || !mainCanvas.width || !mainCanvas.height) return;

    if (!mockup) {
      const ratio = mainCanvas.width / mainCanvas.height;
      const w = ratio > 1 ? 200 : 200 * ratio;
      const h = ratio > 1 ? 200 / ratio : 200;
      ctx.drawImage(mainCanvas, (200 - w) / 2, (200 - h) / 2, w, h);
      return;
    }

    const pa = mockup.printArea;
    const baseX = pa.x * 200;
    const baseY = pa.y * 200;
    const baseW = pa.w * 200;
    const baseH = pa.h * 200;
    const printW = baseW * this.printScale;
    const printH = baseH * this.printScale;
    const printX = baseX + (baseW - printW) / 2;
    const printY = baseY + (baseH - printH) / 2;

    ctx.save();
    ctx.beginPath();
    ctx.rect(baseX, baseY, baseW, baseH);
    ctx.clip();

    const designRatio = mainCanvas.width / mainCanvas.height;
    let dw = printW, dh = printH;
    if (designRatio > dw / dh) {
      dh = dw / designRatio;
    } else {
      dw = dh * designRatio;
    }
    ctx.drawImage(mainCanvas, printX + (printW - dw) / 2, printY + (printH - dh) / 2, dw, dh);
    ctx.restore();
  }

  private drawProductSilhouette(ctx: CanvasRenderingContext2D, type: string) {
    switch (type) {
      case "canvas": {
        // Wooden frame edge
        ctx.fillStyle = "#8B6914";
        ctx.fillRect(6, 6, 188, 188);
        ctx.fillStyle = "#f5f5f0";
        ctx.fillRect(16, 16, 168, 168);
        break;
      }
      case "acrylic": {
        // Glossy acrylic
        ctx.fillStyle = "#e8edf4";
        ctx.fillRect(4, 4, 192, 192);
        const gloss = ctx.createLinearGradient(4, 4, 100, 60);
        gloss.addColorStop(0, "rgba(255,255,255,0.55)");
        gloss.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = gloss;
        ctx.fillRect(4, 4, 192, 56);
        ctx.strokeStyle = "#b8c5d6";
        ctx.lineWidth = 1;
        ctx.strokeRect(4, 4, 192, 192);
        break;
      }
      case "foam": {
        // Foam board — white with thick edge shadow
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(5, 5, 190, 190);
        ctx.strokeStyle = "#c8d0da";
        ctx.lineWidth = 4;
        ctx.strokeRect(5, 5, 190, 190);
        break;
      }
      case "tshirt":
        this.drawGarment(ctx, false);
        break;
      case "hoodie":
        this.drawGarment(ctx, true);
        break;
      case "tote":
        this.drawTote(ctx);
        break;
      case "mug":
        this.drawMug(ctx);
        break;
      default: {
        // poster — white paper with subtle shadow
        ctx.shadowColor = "rgba(0,0,0,0.12)";
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 2;
        ctx.shadowOffsetY = 2;
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(8, 8, 184, 184);
        ctx.shadowColor = "transparent";
        ctx.strokeStyle = "#d1d5db";
        ctx.lineWidth = 1;
        ctx.strokeRect(8, 8, 184, 184);
        break;
      }
    }
  }

  private drawGarment(ctx: CanvasRenderingContext2D, withHood: boolean) {
    ctx.fillStyle = "#f8f8f8";
    ctx.strokeStyle = "#c8d0da";
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(82, 48);
    ctx.bezierCurveTo(82, 68, 118, 68, 118, 48);
    ctx.lineTo(155, 58);
    ctx.lineTo(192, 82);
    ctx.lineTo(188, 104);
    ctx.lineTo(155, 118);
    ctx.lineTo(156, 188);
    ctx.lineTo(44, 188);
    ctx.lineTo(45, 118);
    ctx.lineTo(12, 104);
    ctx.lineTo(8, 82);
    ctx.lineTo(45, 58);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    if (withHood) {
      ctx.beginPath();
      ctx.moveTo(78, 50);
      ctx.bezierCurveTo(74, 12, 126, 12, 122, 50);
      ctx.bezierCurveTo(118, 68, 82, 68, 78, 50);
      ctx.fill();
      ctx.stroke();
    }
  }

  private drawTote(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = "#f5f0e0";
    ctx.strokeStyle = "#c8b89a";
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(28, 72);
    ctx.lineTo(28, 182);
    ctx.lineTo(172, 182);
    ctx.lineTo(172, 72);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(66, 72);
    ctx.bezierCurveTo(62, 28, 80, 16, 100, 16);
    ctx.bezierCurveTo(120, 16, 138, 28, 134, 72);
    ctx.lineWidth = 8;
    ctx.strokeStyle = "#c8b89a";
    ctx.stroke();
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#f5f0e0";
    ctx.stroke();
  }

  private drawMug(ctx: CanvasRenderingContext2D) {
    ctx.fillStyle = "#ffffff";
    ctx.strokeStyle = "#c8d0da";
    ctx.lineWidth = 1.5;

    ctx.beginPath();
    ctx.moveTo(24, 50);
    ctx.lineTo(20, 170);
    ctx.lineTo(148, 170);
    ctx.lineTo(144, 50);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    ctx.beginPath();
    ctx.ellipse(84, 50, 60, 10, 0, 0, Math.PI * 2);
    ctx.fillStyle = "#dde3ea";
    ctx.fill();
    ctx.strokeStyle = "#c8d0da";
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(148, 88);
    ctx.bezierCurveTo(192, 90, 192, 152, 148, 154);
    ctx.lineWidth = 10;
    ctx.strokeStyle = "#c8d0da";
    ctx.stroke();
    ctx.lineWidth = 6;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke();
  }

  private async updatePrice() {
    if (!this.selectedProduct || !this.selectedSizeId) return;

    try {
      const resp = await fetch(
        `/api/pod/price?templateId=${this.selectedProduct.id}&sizeId=${this.selectedSizeId}&quantity=${this.quantity}`,
      );
      if (!resp.ok) return;
      this.currentPrice = await resp.json();

      const priceDisplay = document.getElementById("pod-price-display");

      if (priceDisplay) priceDisplay.textContent = this.currentPrice!.formattedPrice;
    } catch (e) {
      console.error("Price calculation failed:", e);
    }
  }

  private updateQuantityDisplay() {
    const el = document.getElementById("pod-qty-value");
    if (el) el.textContent = String(this.quantity);
  }

  // ─── Checkout Step ──────────────────────────────────────────────

  private showCheckoutStep() {
    const stripeBadge = document.getElementById("pod-stripe-badge");
    if (stripeBadge) stripeBadge.classList.toggle("hidden", this.isDemoMode);

    const summaryProduct = document.getElementById("pod-summary-product");
    const summaryPrice = document.getElementById("pod-summary-price");

    if (summaryProduct && this.selectedProduct) {
      const size = this.selectedProduct.sizes.find((s) => s.id === this.selectedSizeId);
      summaryProduct.textContent = `${this.selectedProduct.name} (${
        size?.label || this.selectedSizeId
      }) × ${this.quantity}`;
    }
    if (summaryPrice && this.currentPrice) {
      summaryPrice.textContent = this.currentPrice.formattedPrice;
    }

    // Load saved address
    this.loadShippingAddress();
  }

  private showPaymentStep() {
    this.renderOrderSummary("pod-payment-summary-product", "pod-payment-summary-price");

    const modeEl = document.getElementById("pod-payment-mode");
    if (modeEl) {
      const key = this.stripeMode === "live"
        ? "pod.paymentModeLive"
        : this.stripeMode === "test"
        ? "pod.paymentModeTest"
        : "pod.paymentModeDemo";
      modeEl.textContent = this.tr(key);
    }
  }

  private saveShippingAddress(address: any) {
    localStorage.setItem(
      "mtp_shipping_address",
      JSON.stringify({
        firstName: address.firstName,
        lastName: address.lastName,
        email: address.email,
        address1: address.addressLine1,
        address2: address.addressLine2,
        city: address.city,
        postcode: address.postcode,
        state: address.state,
        country: address.country,
      }),
    );
  }

  private loadShippingAddress() {
    try {
      const saved = localStorage.getItem("mtp_shipping_address");
      if (!saved) return;
      const addr = JSON.parse(saved);
      this.shippingCountry = addr.country || this.shippingCountry;
      this.syncCountryControls();

      const fields = [
        "first-name",
        "last-name",
        "email",
        "address1",
        "address2",
        "city",
        "postcode",
        "state",
        "country",
      ];
      fields.forEach((f) => {
        const el = document.getElementById(`pod-${f}`) as HTMLInputElement | HTMLSelectElement;
        const key = f.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        const legacyKey = f === "address1"
          ? "addressLine1"
          : f === "address2"
          ? "addressLine2"
          : key;
        const value = addr[key] ?? addr[legacyKey];
        if (el && value) {
          el.value = value;
        }
      });
    } catch (e) {
      console.error("Failed to load saved address:", e);
    }
  }

  private getSavedShippingCountry(): string {
    try {
      const saved = localStorage.getItem("mtp_shipping_address");
      if (!saved) return this.shippingCountry;
      const addr = JSON.parse(saved);
      return addr.country || this.shippingCountry;
    } catch {
      return this.shippingCountry;
    }
  }

  private syncCountryControls() {
    const productCountry = document.getElementById("pod-product-country") as HTMLSelectElement;
    const checkoutCountry = document.getElementById("pod-country") as HTMLSelectElement;
    if (productCountry) productCountry.value = this.shippingCountry;
    if (checkoutCountry) checkoutCountry.value = this.shippingCountry;
  }

  private getCountryName(country: string): string {
    const select = document.getElementById("pod-product-country") as HTMLSelectElement;
    const option = select?.querySelector(`option[value="${country}"]`) as HTMLOptionElement | null;
    return option?.textContent || country;
  }

  private updateEnvironmentBadge() {
    const badge = document.getElementById("pod-demo-badge");
    if (!badge) return;

    const text = badge.querySelector("[data-i18n='pod.demoMode']") as HTMLElement | null;
    if (this.isDemoMode) {
      badge.classList.remove("hidden");
      if (text) text.textContent = this.tr("pod.demoMode");
      return;
    }

    if (this.stripeMode === "test") {
      badge.classList.remove("hidden");
      if (text) text.textContent = this.tr("pod.testMode");
      return;
    }

    if (!this.stripeReady) {
      badge.classList.remove("hidden");
      if (text) text.textContent = this.tr("pod.paymentNotConfigured");
      return;
    }

    badge.classList.add("hidden");
  }

  private renderOrderSummary(productId: string, priceId: string) {
    const summaryProduct = document.getElementById(productId);
    const summaryPrice = document.getElementById(priceId);

    if (summaryProduct && this.selectedProduct) {
      const size = this.selectedProduct.sizes.find((s) => s.id === this.selectedSizeId);
      summaryProduct.textContent = `${this.selectedProduct.name} (${
        size?.label || this.selectedSizeId
      }) × ${this.quantity}`;
    }
    if (summaryPrice && this.currentPrice) {
      summaryPrice.textContent = this.currentPrice.formattedPrice;
    }
  }

  // ─── Order Submission ───────────────────────────────────────────

  private getShippingAddress() {
    return {
      firstName: (document.getElementById("pod-first-name") as HTMLInputElement)?.value || "",
      lastName: (document.getElementById("pod-last-name") as HTMLInputElement)?.value || "",
      email: (document.getElementById("pod-email") as HTMLInputElement)?.value || "",
      addressLine1: (document.getElementById("pod-address1") as HTMLInputElement)?.value || "",
      addressLine2: (document.getElementById("pod-address2") as HTMLInputElement)?.value || "",
      city: (document.getElementById("pod-city") as HTMLInputElement)?.value || "",
      postcode: (document.getElementById("pod-postcode") as HTMLInputElement)?.value || "",
      state: (document.getElementById("pod-state") as HTMLInputElement)?.value || "",
      country: (document.getElementById("pod-country") as HTMLSelectElement)?.value || "DK",
    };
  }

  private buildItems() {
    return [
      {
        id: `item-${Date.now()}`,
        templateId: this.selectedProduct!.id,
        sizeId: this.selectedSizeId,
        quantity: this.quantity,
        designAssetId: this.designAssetId,
        price: this.currentPrice!,
        productName: this.selectedProduct!.name,
        sizeLabel: this.selectedProduct!.sizes.find((s) => s.id === this.selectedSizeId)?.label ||
          this.selectedSizeId,
      },
    ];
  }

  private validateShippingStep(shippingAddress: any): boolean {
    const termsCheck = document.getElementById("pod-terms-check") as HTMLInputElement;
    if (!termsCheck || !termsCheck.checked) {
      alert(this.tr("pod.acceptTerms"));
      return false;
    }

    if (
      !shippingAddress.firstName || !shippingAddress.lastName || !shippingAddress.email ||
      !shippingAddress.addressLine1 || !shippingAddress.city || !shippingAddress.postcode
    ) {
      alert(this.tr("pod.fillRequired"));
      return false;
    }

    return true;
  }

  private async preparePaymentOrDemoOrder() {
    const actionBtn = document.getElementById("pod-action-btn") as HTMLButtonElement;
    const actionText = document.getElementById("pod-action-text");
    if (actionBtn) actionBtn.disabled = true;
    if (actionText) {
      actionText.textContent = this.isDemoMode
        ? this.tr("pod.placingOrder")
        : this.tr("pod.preparingPayment");
    }

    try {
      const shippingAddress = this.getShippingAddress();
      if (!this.validateShippingStep(shippingAddress)) {
        if (actionBtn) actionBtn.disabled = false;
        if (actionText) {
          actionText.textContent = this.isDemoMode
            ? this.tr("pod.placeDemoOrder")
            : this.tr("pod.reviewPayment");
        }
        return;
      }

      // Export design asset
      if (!this.designAssetId) {
        this.designAssetId = await this.getDesignAssetId(this.printScale);
      }

      const items = this.buildItems();
      this.saveShippingAddress(shippingAddress);
      this.lockedShippingAddress = shippingAddress;
      this.lockedItems = items;

      if (this.isDemoMode) {
        // Demo mode: place order directly without payment
        const resp = await fetch("/api/pod/order", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items, shippingAddress, designAssetId: this.designAssetId }),
        });
        if (!resp.ok) {
          const err = await resp.json();
          throw new Error(err.error || "Order failed");
        }
        const result = await resp.json();
        this.showConfirmation(result);
      } else {
        this.goToStep("payment");
        this.updateActionButton();
      }
    } catch (err: any) {
      console.error("Order error:", err);
      alert(this.tr("pod.orderFailed", { message: err.message }));
      if (actionBtn) actionBtn.disabled = false;
      if (actionText) {
        actionText.textContent = this.isDemoMode
          ? this.tr("pod.placeDemoOrder")
          : this.tr("pod.reviewPayment");
      }
    }
  }

  private async submitPayment() {
    const actionBtn = document.getElementById("pod-action-btn") as HTMLButtonElement;
    const actionText = document.getElementById("pod-action-text");
    if (actionBtn) actionBtn.disabled = true;
    if (actionText) actionText.textContent = this.tr("pod.redirectingToPayment");

    try {
      const resp = await fetch("/api/pod/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: this.lockedItems,
          shippingAddress: this.lockedShippingAddress,
          designAssetId: this.designAssetId,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || "Checkout failed");
      }
      const { checkoutUrl } = await resp.json();
      globalThis.location.href = checkoutUrl;
    } catch (err: any) {
      console.error("Payment error:", err);
      alert(this.tr("pod.orderFailed", { message: err.message }));
      if (actionBtn) actionBtn.disabled = false;
      if (actionText) actionText.textContent = this.tr("pod.continueToPayment");
    }
  }

  // Called by App.ts after Stripe redirects back with a paid session
  async completePayment(sessionId: string) {
    if (!this.modal) return;

    this.modal.classList.remove("hidden");
    document.body.style.overflow = "hidden";

    this.goToStep("confirm");
    const orderIdEl = document.getElementById("pod-order-id");
    const totalEl = document.getElementById("pod-confirm-total");
    const deliveryEl = document.getElementById("pod-confirm-delivery");
    if (orderIdEl) orderIdEl.textContent = this.tr("pod.completingOrder");
    if (totalEl) totalEl.textContent = "...";
    if (deliveryEl) deliveryEl.textContent = "";
    this.updateActionButton();

    try {
      const resp = await fetch("/api/pod/complete-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId }),
      });
      if (!resp.ok) {
        const err = await resp.json();
        throw new Error(err.error || "Order completion failed");
      }
      const result = await resp.json();
      this.showConfirmation(result);
    } catch (err: any) {
      console.error("Complete payment error:", err);
      alert(this.tr("pod.orderFailed", { message: err.message }));
      this.closeNow();
    }
  }

  private showConfirmation(result: any) {
    const orderIdEl = document.getElementById("pod-order-id");
    const totalEl = document.getElementById("pod-confirm-total");
    const deliveryEl = document.getElementById("pod-confirm-delivery");

    if (orderIdEl) orderIdEl.textContent = this.tr("pod.orderNumber", { orderId: result.orderId });
    if (totalEl) totalEl.textContent = `€${result.totalCharged.toFixed(2)}`;
    if (deliveryEl) {
      deliveryEl.textContent = result.estimatedDelivery
        ? this.localizeDeliveryEstimate(result.estimatedDelivery)
        : this.tr("pod.defaultDelivery");
    }

    this.goToStep("confirm");
    this.updateActionButton();
  }

  // ─── Step Navigation ────────────────────────────────────────────

  private goToStep(step: "product" | "config" | "checkout" | "payment" | "confirm") {
    this.currentStep = step;
    this.modal.querySelectorAll(".pod-step").forEach((el) => el.classList.add("hidden"));

    const stepEl = document.getElementById(`pod-step-${step}`);
    if (stepEl) stepEl.classList.remove("hidden");

    if (step === "product") this.showProductStep();
    if (step === "config") this.showConfigStep();
    if (step === "checkout") this.showCheckoutStep();
    if (step === "payment") this.showPaymentStep();
  }

  private showProductStep() {
    const comingSoon = document.getElementById("pod-coming-soon");
    const catTabs = document.getElementById("pod-cat-tabs");
    const productGrid = document.getElementById("pod-product-grid");
    const actionBtn = document.getElementById("pod-action-btn");

    if (!this.allowOrderPhysical) {
      if (comingSoon) comingSoon.style.display = "block";
      if (catTabs) catTabs.style.display = "none";
      if (productGrid) productGrid.style.display = "none";
      if (actionBtn) actionBtn.style.display = "none";
    } else {
      if (comingSoon) comingSoon.style.display = "none";
      if (catTabs) catTabs.style.removeProperty("display");
      if (productGrid) productGrid.style.removeProperty("display");
      if (actionBtn) actionBtn.style.removeProperty("display");
    }
  }

  private updateActionButton() {
    const btn = document.getElementById("pod-action-btn") as HTMLButtonElement;
    const text = document.getElementById("pod-action-text");
    if (!btn || !text) return;

    switch (this.currentStep) {
      case "product":
        btn.disabled = !this.selectedProduct;
        text.textContent = this.selectedProduct
          ? this.tr("pod.configure")
          : this.tr("pod.selectProduct");
        break;
      case "config":
        btn.disabled = !this.selectedSizeId;
        text.textContent = this.tr("pod.proceedCheckout");
        break;
      case "checkout": {
        btn.disabled = false;
        text.textContent = this.isDemoMode
          ? this.tr("pod.placeDemoOrder")
          : this.tr("pod.reviewPayment");
        break;
      }
      case "payment": {
        btn.disabled = !this.stripeReady;
        text.textContent = this.tr("pod.continueToPayment");
        break;
      }
      case "confirm":
        btn.disabled = false;
        text.textContent = this.tr("pod.done");
        break;
    }
  }

  private tr(key: string, replacements: Record<string, any> = {}): string {
    return t(key, localStorage.getItem("lang") || "en", replacements);
  }

  private applyTranslations() {
    const lang = localStorage.getItem("lang") || "en";

    this.modal.querySelectorAll("[data-i18n]").forEach((el: any) => {
      const key = el.dataset.i18n;
      const translated = t(key, lang);
      if (translated && translated !== key) {
        el.textContent = translated;
      }
    });

    this.modal.querySelectorAll("[data-i18n-placeholder]").forEach((el: any) => {
      const key = el.dataset.i18nPlaceholder;
      const translated = t(key, lang);
      if (translated && translated !== key) {
        el.placeholder = translated;
      }
    });
  }

  private localizeDeliveryEstimate(value: string): string {
    const lang = localStorage.getItem("lang") || "en";
    if (lang !== "da") return value;
    return value.replace("business days", "hverdage").replace("business day", "hverdag");
  }

  private handleAction() {
    switch (this.currentStep) {
      case "product":
        if (this.selectedProduct) {
          this.goToStep("config");
          this.updateActionButton();
        }
        break;
      case "config":
        this.goToStep("checkout");
        this.updateActionButton();
        break;
      case "checkout":
        this.preparePaymentOrDemoOrder();
        break;
      case "payment":
        this.submitPayment();
        break;
      case "confirm":
        this.closeNow();
        break;
    }
  }
}
